import axios from "axios";
const REACT_APP_ADMIN_API = process.env.REACT_APP_ADMIN_API;
const REACT_APP_VEHICLES_API = process.env.REACT_APP_VEHICLES_API;
const { CancelToken } = axios;
const source = CancelToken.source();
import { Config } from "../../pages/config/Config";

export enum ConfigFilters {
  Version = "filter_version",
  HardwareType = "filter_hardware_type",
  SimType = "filter_sim_type",
  Imei = "filter_imei",
  NoImei = "filter_no_imei",
  HasDownloadedAt = "filter_has_downloaded_at",
  Description = "filter_description",
  Tags = "filter_tags",
  CreatedAt = "filter_created_at",
  Customer = "filter_customer",
}

export const getConfigs = async (
  query: string,
  page: number,
  cancelToken = source.token,
  filterBy: string = "", // Configuration filters: "version", "hardware_type", "sim_type", "imei", "no_imei", "has_downloaded_at", "description", "tags", "created_at", "customer"
  sortBy: string = "",
  count: number = 100,
) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  let url = `${REACT_APP_ADMIN_API}/api/configurations?results_per_page=${count}&q=${query}&page=${page}`;
  if (sortBy !== "") {
    url = `${url}&order=${sortBy}`;
  }
  if (filterBy !== "") {
    url = `${url}&${filterBy}&exclude_contents=1`;
  }
  try {
    const response = await axios({
      method: "get",
      url,
      headers: { Authorization: `Bearer ${token}` },
      cancelToken,
    });
    return { data: response.data, hasNextPage: response.data.has_next_page };
  } catch (e) {
    return e;
  }
};

export const getConfig = async (id) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const region = localStorage.getItem("region");
  try {
    const response = await axios({
      method: "get",
      url: `${REACT_APP_ADMIN_API}/api/configurations/${id}`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response.data };
  } catch (e) {
    return e;
  }
};

export const downloadConfigs = async (imeis: string[]) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/device/getconfig`,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        imeis,
      },
    });
    return { data: response.data };
  } catch (e) {
    return e;
  }
};

export const downloadConfig = async (id: string) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "get",
      url: `${REACT_APP_ADMIN_API}/api/configurations/${id}/download`,
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    return { data: response.data };
  } catch (e) {
    return e;
  }
};

export const downloadConfigByImei = async (imei: string) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const c = await getConfigs(
      imei,
      1,
      source.token,
      "filter_has_downloaded_at",
      "downloaded_at DESC",
      1,
    );
    if (c.data && c.data?.data?.length > 0) {
      const response = await axios({
        method: "get",
        url: `${REACT_APP_ADMIN_API}/api/configurations/${c.data.data[0].id}/download`,
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      return { data: response.data };
    } else {
      return {};
    }
  } catch (e) {
    return e;
  }
};

export const createConfig = async ({
  hardwareType,
  version,
  contentsBase64,
  description,
  tags,
  allowedCustomers,
  simType,
}: Config) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/configurations`,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        hardware_type: hardwareType,
        version,
        contents_base64: contentsBase64,
        tags,
        description,
        allowed_customers: allowedCustomers,
        sim_type: simType?.toLowerCase(),
      },
    });
    return { data: response };
  } catch (e) {
    return { detail: e.response.data.error };
  }
};

export const deleteConfig = async (id: string) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "delete",
      url: `${REACT_APP_ADMIN_API}/api/configurations/${id}`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response };
  } catch (e) {
    return { error: e.response.data.error };
  }
};

export const loadConfigToDevices = async (imeis: string[], id: number) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/configurations/${id}/load`,
      headers: { Authorization: `Bearer ${token}` },
      data: imeis,
    });
    return { data: response };
  } catch (e) {
    return { detail: e.response.data.error };
  }
};

export const cancelConfigUpdate = async (imeis) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/configurations/cancel`,
      headers: { Authorization: `Bearer ${token}` },
      data: imeis,
    });
    return { data: response };
  } catch (e) {
    return { detail: e.response.data.error };
  }
};

export const editConfig = async ({
  id,
  hardwareType,
  version,
  contentsBase64,
  description,
  tags,
  allowedCustomers,
  simType,
}: Config) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "patch",
      url: `${REACT_APP_ADMIN_API}/api/configurations/${id}`,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        hardware_type: hardwareType,
        version,
        contents_base64: contentsBase64,
        tags,
        description,
        allowed_customers: allowedCustomers,
        sim_type: simType?.toLowerCase(),
      },
    });
    return { data: response };
  } catch (e) {
    return { error: e.response.data.error };
  }
};
